import axios, { baseUrl } from "../axios/axios";
import {
  AdvisorProfileRequest,
  AdvisorProfileResponse,
  AdvisorWorkingHoursEntry,
  AdvisoryAreaResponse,
} from "../types/advisorProfile.types";

// ITAM-2887 advisor-profile service. Every endpoint is open to ADVISOR_USER,
// and this branch's axios instance has no 401/403 logout interceptor, so the
// shared instance is used throughout. The logout-time pause lives in
// sessionSideEffects.ts.

const PROFILE_ENDPOINT = "/advisor_profile";

// The media helpers and limits live in mediaPayload.ts (shared with the
// association media card); re-exported so no importer had to move.
export {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  buildImagesCreate,
  buildImagesUpdate,
  mediaTypeOf,
  validateMediaFile,
} from "./mediaPayload";
export type { ImagesUpdate, PendingMediaFile } from "./mediaPayload";

/**
 * GET the profile. 200 -> data, 404 -> `null` (no profile row yet: the normal
 * onboarding state, NOT an error), anything else rethrows, so a reducer can
 * never mistake a network failure for a missing profile.
 */
export const getAdvisorProfile = (
  userId: number,
  signal?: AbortSignal,
): Promise<AdvisorProfileResponse | null> => {
  return axios
    .get<AdvisorProfileResponse>(`${PROFILE_ENDPOINT}/user_id/${userId}`, {
      signal,
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    });
};

/** GET /advisory_area: ordered list, any signed-in role. */
export const getAdvisoryAreas = (
  signal?: AbortSignal,
): Promise<AdvisoryAreaResponse[]> =>
  axios
    .get<AdvisoryAreaResponse[]>("/advisory_area", { signal })
    .then((res) => res.data);

const multipart = (request: AdvisorProfileRequest, files: File[]) => {
  const formData = new FormData();
  formData.append("advisorProfileRequestString", JSON.stringify(request));
  files.forEach((file) => {
    formData.append("images", file);
  });
  return formData;
};

/**
 * CREATE (201). Rows and files pair up IN ORDER (every row consumes the next
 * uploaded file), so the request's image rows must be in the same order as
 * the appended files.
 */
export const createAdvisorProfile = async (
  request: AdvisorProfileRequest,
  files: File[],
): Promise<void> => {
  await axios.post(`${baseUrl}${PROFILE_ENDPOINT}`, multipart(request, files), {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * UPDATE. Overwrites the scalar fields unconditionally, so always send the
 * complete set. Media is touched only when `updateImages: true`, and then
 * the row set is rebuilt from `images`: rows whose `location` matches a
 * stored location are kept, every other row consumes the next uploaded file.
 */
export const updateAdvisorProfile = async (
  request: AdvisorProfileRequest,
  files: File[],
): Promise<void> => {
  await axios.put(`${baseUrl}${PROFILE_ENDPOINT}`, multipart(request, files), {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** PUT replaces the WHOLE schedule; `[]` clears it. Times strictly "HH:mm". */
export const updateAdvisorWorkingHours = async (
  userId: number,
  entries: AdvisorWorkingHoursEntry[],
): Promise<void> => {
  await axios.put(`${PROFILE_ENDPOINT}/user_id/${userId}/working_hours`, entries);
};

/**
 * `available=true` is enrol AND resume (one endpoint); `available=false` is
 * pause. Serves activation, resume and the in-app pause control, but NOT the
 * logout pause (sessionSideEffects.ts).
 */
export const setAdvisorAvailability = async (
  userId: number,
  available: boolean,
): Promise<void> => {
  await axios.put(
    `${PROFILE_ENDPOINT}/user_id/${userId}/availability?available=${available}`,
  );
};

// NOTE: DELETE /advisor_profile/user_id/{id} and DELETE .../support exist but
// are deliberately NOT exposed (Q4).
